import React, { useState, useRef, useEffect } from 'react';
import './Hero.css';
import { FaCalendarAlt, FaPlay, FaPause } from 'react-icons/fa';
import DemoForm from './DemoForm';

const Hero = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState('');
  const [progress, setProgress] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const oscillatorRef = useRef(null);

  // Load voices when component mounts
  useEffect(() => {
    if (window.speechSynthesis) {
      // Load voices
      const loadVoices = () => {
        const voices = speechSynthesis.getVoices();
       
      };
      speechSynthesis.addEventListener('voiceschanged', loadVoices);
      loadVoices();
      return () => {
        speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      };
    }
    // Try to play audio immediately on mount
    const tryAutoPlay = async () => {
      if (audioRef.current) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          // Autoplay was blocked, show play icon and let user start
          setIsPlaying(false);
        }
      }
    };
    tryAutoPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createAIConversationAudio = async () => {
    try {
      // Try to use the actual audio file first
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
        return true;
      }
      
      // Fallback to speech synthesis if audio file doesn't work
      if (!window.speechSynthesis) {
        throw new Error('No audio options available');
      }

      // Load conversation data for speech synthesis fallback
      const response = await fetch('/demo-conversation.json');
      const data = await response.json();
      const conversation = data.conversation;
      
      let currentDelay = 0;
      const utterances = [];
      
      conversation.forEach((segment, index) => {
        setTimeout(() => {
          if (isPlaying) {
            setConversationState({
              speaker: segment.speaker,
              text: segment.text,
              isLast: index === conversation.length - 1
            });
            
            const utterance = new SpeechSynthesisUtterance(segment.text);
            
            if (segment.speaker === 'AI') {
              utterance.pitch = 1.1;
              utterance.rate = 0.9;
              utterance.volume = 0.8;
              const voices = speechSynthesis.getVoices();
              const femaleVoice = voices.find(voice => 
                voice.name.includes('Female') || 
                voice.name.includes('Zira') || 
                voice.name.includes('Samantha') ||
                voice.gender === 'female'
              );
              if (femaleVoice) {
                utterance.voice = femaleVoice;
              }
            } else {
              utterance.pitch = 0.9;
              utterance.rate = 1.0;
              utterance.volume = 0.7;
              const voices = speechSynthesis.getVoices();
              const maleVoice = voices.find(voice => 
                voice.name.includes('Male') || 
                voice.name.includes('David') || 
                voice.name.includes('Daniel') ||
                voice.gender === 'male'
              );
              if (maleVoice) {
                utterance.voice = maleVoice;
              }
            }
            
            const progressPercent = ((segment.timestamp + segment.duration) / data.totalDuration) * 100;
            setProgress(progressPercent);
            
            speechSynthesis.speak(utterance);
            utterances.push(utterance);
            
            if (index === conversation.length - 1) {
              utterance.onend = () => {
                setTimeout(() => {
                  setIsPlaying(false);
                  setIsLoading(false);
                  setConversationState('');
                  setProgress(0);
                }, 1000);
              };
            }
          }
        }, segment.timestamp * 1000);
      });
      
      oscillatorRef.current = { 
        stop: () => {
          speechSynthesis.cancel();
          utterances.forEach(utterance => {
            utterance.onend = null;
          });
          setConversationState('');
          setProgress(0);
        }
      };
      
      return true;
    } catch (error) {
      console.log('Audio playback failed:', error);
      return false;
    }
  };

  const togglePlayPause = async () => {
    try {
      if (isPlaying) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        setIsPlaying(false);
      } else {
        if (audioRef.current) {
          await audioRef.current.play();
          setIsPlaying(true);
        }
      }
    } catch (error) {
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setIsLoading(false);
    setConversationState('');
    setProgress(0);
  };

  const handleAudioPause = () => {
    setIsPlaying(false);
    setIsLoading(false);
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      if (duration > 0) {
        setProgress((currentTime / duration) * 100);
      }
    }
  };

  const handleDemoClick = (e) => {
    e.preventDefault();
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // Here you would handle form submission, e.g., send data to a server
    console.log('Form submitted');
    setShowForm(false);
    window.open('http://calendly.com/', '_blank');
  };

  return (
    <section className="hero">
      <div className="hero-container">        <h1 className="hero-title">AI Receptionist of Aesthetic Clinic</h1>
        <p className="hero-subtitle">
          Never lose a new patient to voicemail again.
        </p>
        <div className="hero-features">
          <span className="hero-feature">24/7 Scheduling</span>
          <span className="hero-feature">Keep Records</span>
          <span className="hero-feature">Treatment Info</span>
          <span className="hero-feature">Follow up</span>
          <span className="hero-feature">Feedback</span>
        </div>
        
        <div className="hero-video">
          <div className="audio-container">
            <div className="audio-player-center">
              {/* <audio 
                ref={audioRef}
                onEnded={handleAudioEnded}
                onPause={handleAudioPause}
                onTimeUpdate={handleAudioTimeUpdate}
                preload="auto"
              >
                <source src="/audio.wav" type="audio/wav" />
                Your browser does not support the audio element.
              </audio> */}





            <video 
              controls
              preload="metadata"
              className="w-full h-full"
            >
              <source src="https://storage.googleapis.com/upsurge-public-assets/aesthetics-ai-artifacts/aesthetic_video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>


              
              {/* <div className="audio-player-container">
                <button onClick={togglePlayPause} className={`audio-player-button ${isPlaying ? 'playing' : ''}`}>
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </button>
                <div className="audio-waveform">
                  {Array.from({ length: 60 }).map((_, i) => (
                    <div 
                      key={i} 
                      className="waveform-bar" 
                      style={{ height: `${isPlaying ? Math.random() * 80 + 5 : 5}%` }}
                    ></div>
                  ))}
                </div>
              </div> */}

              <div className="audio-text">
                <p className="microphone-instruction">
                  Listen to a demo call to see how our AI works.
                </p>
                
                {/* Captions Display */}
                {conversationState && (
                  <div className="audio-captions">
                    <div className={`caption ${conversationState.speaker?.toLowerCase()}`}>
                      <span className="speaker">{conversationState.speaker}:</span>
                      <span className="caption-text">{conversationState.text}</span>
                    </div>
                  </div>
                )}
                
                <div className="demo-button-container">
                  <a href="http://calendly.com/" className="demo-button" onClick={handleDemoClick}>
                    <FaCalendarAlt />
                    Book a Free Demo
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showForm && <DemoForm onClose={handleFormClose} onSubmit={handleFormSubmit} />}
    </section>
  );
};

export default Hero;